import type {
  QuizSession,
  QuizAnswer,
  API
} from '@solace-quiz/shared';

const azureFunctionsUrl = import.meta.env.VITE_AZURE_FUNCTIONS_URL || 'http://localhost:7071/api';

class PublicAzureClient {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const response = await fetch(`${azureFunctionsUrl}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  // Sessions (public methods only)
  sessions = {
    create: async (data: API.Sessions.CreateRequest): Promise<QuizSession> => {
      return this.request<QuizSession>('/sessions', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    getById: async (id: string): Promise<QuizSession | null> => {
      try {
        return await this.request<QuizSession>(`/sessions/${id}`);
      } catch (error) {
        return null;
      }
    },

    getByEmail: async (email: string, quizId: string): Promise<QuizSession | null> => {
      return this.request<QuizSession | null>(`/sessions/by-email/${encodeURIComponent(email)}?quiz_id=${quizId}`);
    },

    checkName: async (name: string, quizId: string): Promise<boolean> => {
      const result = await this.request<{ exists: boolean }>(`/sessions/check-name/${encodeURIComponent(name)}?quiz_id=${quizId}`);
      return result.exists;
    },

    update: async (id: string, data: API.Sessions.UpdateRequest): Promise<QuizSession> => {
      return this.request<QuizSession>(`/sessions/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },
  };

  // Answers (public methods only)
  answers = {
    create: async (data: API.Answers.CreateRequest): Promise<QuizAnswer> => {
      return this.request<QuizAnswer>('/answers', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
  };

  // Email
  email = {
    sendVerification: async (data: API.Email.SendVerificationRequest): Promise<API.Email.SendVerificationResponse> => {
      return this.request<API.Email.SendVerificationResponse>('/send-verification-email', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
  };

  // Leaderboard
  leaderboard = {
    get: async (quizId: string): Promise<API.Leaderboard.GetResponse> => {
      return this.request<API.Leaderboard.GetResponse>(`/leaderboard/${quizId}`);
    },
  };

  // Quiz (public methods only)
  quiz = {
    get: async (quizId: string): Promise<any> => {
      return this.request<any>(`/quizzes/${quizId}`);
    },
  };
}

export const azureClient = new PublicAzureClient();
